import type { GetHistoryEntry } from '@repo/schema/history'
import { format, getTime } from '@repo/sdk/date'

import type { HistoryChangeGroup, HistoryDay } from './types.ts'

export const sortHistoryEntries = (entries: readonly GetHistoryEntry[]) =>
  entries.toSorted((left, right) => {
    const dateDifference = getTime(right.created_at) - getTime(left.created_at)
    return dateDifference || right.id - left.id
  })

// The API records a Duplicate's `duplicated` entry as a change of its Original Load, but it reads
// as an event: "Duplicated from load #X".
const toShownEntry = (entry: GetHistoryEntry): GetHistoryEntry =>
  entry.event === 'duplicated' ? { ...entry, field: null, field_label: null } : entry

const canJoinChangeGroup = (group: HistoryChangeGroup, entry: GetHistoryEntry) =>
  group.user.id === entry.user.id && group.category === entry.category

const canJoinEventGroup = (event: GetHistoryEntry, entry: GetHistoryEntry) =>
  event.user.id === entry.user.id && event.category === entry.category

const toSecond = (date: Date) => Math.floor(getTime(date) / 1000)

// One save writes its rows in the same second, possibly from several records with other
// categories in between (a verification touches the company and its risk profile).
// The search stops at another user's row, so different actors never interleave.
const findSaveGroup = (day: HistoryDay, entry: GetHistoryEntry) => {
  const savedAt = toSecond(entry.created_at)

  for (const item of day.items.toReversed()) {
    const itemUser = item.type === 'changes' ? item.user : item.entry.user
    if (itemUser.id !== entry.user.id) return undefined

    if (
      item.type === 'changes' &&
      item.category === entry.category &&
      item.entries.some(groupEntry => toSecond(groupEntry.created_at) === savedAt)
    ) {
      return item
    }
  }

  return undefined
}

const getNewValue = (entry: GetHistoryEntry) => entry.new_display ?? entry.new_value

// Two records mirroring one field (company and risk profile "Who verified") read as one change.
const isRepeatedChange = (group: HistoryChangeGroup, entry: GetHistoryEntry) =>
  entry.field !== null &&
  group.entries.some(
    groupEntry =>
      groupEntry.field === entry.field &&
      groupEntry.field_label === entry.field_label &&
      getNewValue(groupEntry) === getNewValue(entry),
  )

const addToGroup = (group: HistoryChangeGroup, entry: GetHistoryEntry) => {
  if (!isRepeatedChange(group, entry)) group.entries.push(entry)
}

export const groupHistoryEntries = (entries: readonly GetHistoryEntry[]): HistoryDay[] => {
  const days: HistoryDay[] = []

  for (const entry of sortHistoryEntries(entries).map(toShownEntry)) {
    const dayId = format(entry.created_at, 'yyyy-MM-dd')
    let day = days.at(-1)

    if (!day || day.id !== dayId) {
      day = { date: entry.created_at, id: dayId, items: [] }
      days.push(day)
    }

    const previousItem = day.items.at(-1)
    if (previousItem?.type === 'changes' && canJoinChangeGroup(previousItem, entry)) {
      addToGroup(previousItem, entry)
      continue
    }

    if (previousItem?.type === 'event' && canJoinEventGroup(previousItem.entry, entry)) {
      day.items[day.items.length - 1] = {
        category: previousItem.entry.category,
        createdAt: previousItem.entry.created_at,
        entries: [previousItem.entry, entry],
        id: `changes-${previousItem.entry.id}`,
        type: 'changes',
        user: previousItem.entry.user,
      }
      continue
    }

    const saveGroup = findSaveGroup(day, entry)
    if (saveGroup) {
      addToGroup(saveGroup, entry)
      continue
    }

    if (entry.field === null) {
      day.items.push({ entry, id: `event-${entry.id}`, type: 'event' })
      continue
    }

    day.items.push({
      category: entry.category,
      createdAt: entry.created_at,
      entries: [entry],
      id: `changes-${entry.id}`,
      type: 'changes',
      user: entry.user,
    })
  }

  return days
}
